import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

const KEYBOARD_STEP_PX = 24;
const NARROW_VIEWPORT_PX = 980;

interface Options {
  /** Committed panel width (persisted by the caller, e.g. in the URL). */
  width: number;
  minPanelWidth: number;
  minMainWidth: number;
  /** Resizing is only possible while the panel sits to the right. */
  enabled: boolean;
  onCommit(width: number): void;
}

/**
 * Drag/keyboard resizing for a details panel placed right of the main area.
 * The width follows the pointer while dragging and is committed on release;
 * committed widths are clamped so the main area keeps `minMainWidth`.
 */
export function useResizablePanel({
  width,
  minPanelWidth,
  minMainWidth,
  enabled,
  onCommit,
}: Options) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragWidth, setDragWidth] = useState<number | null>(null);

  const clamp = useCallback(
    (value: number) => {
      const container = containerRef.current;
      if (!Number.isFinite(value)) return minPanelWidth;
      // A hidden container reports width 0; do not clamp against that.
      if (!container?.clientWidth) return Math.max(minPanelWidth, value);
      const style = getComputedStyle(container);
      const track = Number.parseFloat(style.getPropertyValue("--splitter-track-width")) || 12;
      const gap = Number.parseFloat(style.columnGap) || 0;
      const max = Math.max(minPanelWidth, container.clientWidth - minMainWidth - track - gap * 2);
      return Math.max(minPanelWidth, Math.min(max, value));
    },
    [minPanelWidth, minMainWidth],
  );

  // Keep the committed width valid for the current container and viewport.
  useEffect(() => {
    const revalidate = () => {
      const clamped = clamp(width);
      if (clamped !== width) onCommit(clamped);
    };
    revalidate();
    window.addEventListener("resize", revalidate);
    return () => window.removeEventListener("resize", revalidate);
  }, [clamp, width, onCommit, enabled]);

  const isActive = () => enabled && window.innerWidth > NARROW_VIEWPORT_PX;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!isActive()) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const widthAt = (clientX: number) => clamp(startWidth - (clientX - startX));
    document.body.classList.add("details-resizing");
    const onMove = (move: globalThis.PointerEvent) => setDragWidth(widthAt(move.clientX));
    const onUp = (up: globalThis.PointerEvent) => {
      document.body.classList.remove("details-resizing");
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      setDragWidth(null);
      onCommit(widthAt(up.clientX));
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    setDragWidth(startWidth);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isActive() || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
    event.preventDefault();
    onCommit(clamp(width + (event.key === "ArrowLeft" ? KEYBOARD_STEP_PX : -KEYBOARD_STEP_PX)));
  };

  const panelWidth = dragWidth ?? width;
  return {
    containerRef,
    panelWidth,
    splitterProps: {
      role: "separator" as const,
      "aria-orientation": "vertical" as const,
      "aria-valuemin": minPanelWidth,
      "aria-valuenow": Math.round(panelWidth),
      tabIndex: enabled ? 0 : -1,
      "aria-hidden": !enabled,
      className: `matrix-splitter${dragWidth !== null ? " is-dragging" : ""}`,
      onPointerDown,
      onKeyDown,
    },
  };
}
